import { Children, useEffect, useState } from "react";
import { CardsStatics } from "src/Pages/components/CardsStatics";
import PersonIcon from "@mui/icons-material/Person";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Chip, Grid } from "@mui/material";
import { CardHeaderCore } from "src/Pages/components/CardCore/Components/CardHeaderCore";
import { PreviewIcon } from "src/Pages/components/Icons/Icons";
import { TableRowCore, TableCore, TdCore } from "src/Pages/components";
import { ButtonCore } from "src/Pages/components/ButtonCore/ButtonCore";
import { FormatDateBR } from "src/shared/Utils/FormatDateBR";
import { FormFilterAniversariantes } from "./components/Form";
import { useContextReportAniversariantes } from "src/Contexts/ReportAniversariantes/ContextReportAniversariantes";
import { useHttpReportAniversariantesProdutores } from "./Hooks/useHttpReportAniversariantesProdutores";
import { THeadAniversariantes } from "./TableAniversariantes/THeadAniversariantes";
import { handleChipCoreCategories } from "src/Pages/components/CategoryComponentMap";
import { handleDownloadAniversariantesPDF } from "./components/PDF/ReportAniversariantesProdutoresPDF";

export const PageReportAniversariantesProdutores = () => {
  const { dataAniversariantes, filterAniversariantes } =
    useContextReportAniversariantes();
  const { handleGet } = useHttpReportAniversariantesProdutores();
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (filterAniversariantes.startDate && filterAniversariantes.endDate) {
      handleGet();
    }
  }, []);

  const list = dataAniversariantes.data ?? [];
  const total = dataAniversariantes.total ?? 0;

  const todayMMDD = (() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${m}-${day}`;
  })();

  const aniversariantesHoje = list.filter((item) => {
    if (!item.data_nasc) return false;
    const [y, m, d] = item.data_nasc.split("-");
    return `${m}-${d}` === todayMMDD;
  }).length;

  const formatDiaAniversario = (dataNasc: string | undefined) => {
    if (!dataNasc) return "-";
    const [, m, d] = dataNasc.split("-");
    return `${d}/${m}`;
  };

  const diasParaAniversario = (dataNasc: string | undefined): number | null => {
    if (!dataNasc || dataNasc.length < 10) return null;
    const [, mesStr, diaStr] = dataNasc.split("-");
    const mes = Number(mesStr) - 1;
    const dia = Number(diaStr);
    if (Number.isNaN(mes) || Number.isNaN(dia)) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    let proximo = new Date(hoje.getFullYear(), mes, dia);
    proximo.setHours(0, 0, 0, 0);
    if (proximo.getTime() < hoje.getTime()) {
      proximo = new Date(hoje.getFullYear() + 1, mes, dia);
      proximo.setHours(0, 0, 0, 0);
    }
    const diffMs = proximo.getTime() - hoje.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  };

  const labelDiasParaAniversario = (dias: number | null) => {
    if (dias === null) return "-";
    if (dias === 0) return "Hoje";
    if (dias === 1) return "Amanhã";
    return `${dias} dias`;
  };

  const handleExportPdf = async () => {
    if (!filterAniversariantes.startDate || !filterAniversariantes.endDate) return;
    setExportingPdf(true);
    try {
      await handleDownloadAniversariantesPDF({
        list,
        aniversariantesHoje,
        total,
        startDate: filterAniversariantes.startDate,
        endDate: filterAniversariantes.endDate,
      });
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <>
      <CardHeaderCore
        buttonAdd={[
          <ButtonCore
            key="pdf"
            title="Exportar PDF"
            onClick={handleExportPdf}
            disabled={exportingPdf || list.length === 0}
            startIcon={<PictureAsPdfIcon sx={{ fontSize: 18 }} />}
            variant="outlined"
          />,
          <FormFilterAniversariantes key="filter" onFilterSubmit={handleGet} />,
        ]}
        icon={<PreviewIcon color="primary" sx={{ fontSize: "20px" }} />}
        sxStyle={{ margin: "1px 0 16px 0", padding: "12px" }}
        title={`Relatorios de produtores aniversariantes `}
      />

      <Grid container spacing={2}>
        <Grid item md={4} xs={12}>
          <CardsStatics
            data={{
              name: "Aniversariantes hoje",
              value1: {
                value: aniversariantesHoje.toString(),
                valueSub: "produtores",
              },
              icon: (
                <PersonIcon color="primary" sx={{ fontSize: "24px" }} />
              ),
            }}
            small
            tooltip="Quantidade de produtores que fazem aniversário hoje (no período filtrado)"
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <CardsStatics
            data={{
              name: "Aniversariantes no período",
              value1: {
                value: total.toString(),
                valueSub: "produtores",
              },
              icon: (
                <PersonIcon color="success" sx={{ fontSize: "24px" }} />
              ),
            }}
            small
            tooltip="Quantidade de produtores aniversariantes no período filtrado"
          />
        </Grid>
      </Grid>

      <TableCore
        isNotPagination
        THead={<THeadAniversariantes />}
        qtdList={list.length}
        sxStyle={{ margin: "16px 0" }}
      >
        {Children.toArray(
          list.map((item) => {
            const dias = diasParaAniversario(item.data_nasc);
            const ehHoje = dias === 0;
            return (
              <TableRowCore key={item.id}>
                <TdCore
                  isAvatar
                  titleAvatar={item.nome}
                  isLimit
                  isLimitQTD={35}
                  values={`${item.id} - ${item.nome}`}
                  sxStyle={{ fontWeight: "600", padding: "4px 0", height: "50px", display: "flex", alignItems: "center" }}
                />
                <TdCore
                  textAlign="center"
                  values={
                    <Chip
                      label={formatDiaAniversario(item.data_nasc)}
                      color={ehHoje ? "success" : "primary"}
                      size="small"
                      sx={{ minWidth: "56px" }}
                    />
                  }
                />
                <TdCore
                  textAlign="center"
                  values={
                    <Chip
                      label={labelDiasParaAniversario(dias)}
                      color={ehHoje ? "success" : "default"}
                      size="small"
                      variant={ehHoje ? "filled" : "outlined"}
                      sx={{ minWidth: "56px" }}
                    />
                  }
                />
                <TdCore values={handleChipCoreCategories(item.funcao_produtor)} />
                <TdCore values={handleChipCoreCategories(item.tipo_produtor)} />
                <TdCore
                  isLimit
                  isLimitQTD={35}
                  values={item.email || item.email_contato || "-"}
                />
                <TdCore values={item.telefone1 || item.telefone2 || "-"} />
                <TdCore values={FormatDateBR(item.created_at)} textAlign="center" />
              </TableRowCore>
            );
          })
        )}
      </TableCore>
    </>
  );
};